import { useState, useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";
import { useSearchExercises, useAssignHomework, useFetchReviewHomework } from "../../../../services/apis";
import { openSandboxAsMentor } from "../../../../../../utils/cySso";
import { fireEpicenterGtm } from "../../../../../../datalayers";


const useHomeworkModal = (student, isOpen, subject) => {
  const [exerciseList, setExerciseList] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [urlArray, setUrlArray] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [urlValue, setUrlValue] = useState("");
  const [urlTitle, setUrlTitle] = useState("");
  const [selectedAiHomework, setSelectedAiHomework] = useState([]);
  const [failedTasksPopup, setFailedTasksPopup] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [isLoadingReview, setIsLoadingReview] = useState(false);

  const { mutate: searchExercises } = useSearchExercises();
  const { mutate: assignHomework } = useAssignHomework();
  const { mutate: fetchReviewHomework } = useFetchReviewHomework();

  const defaultSearchKeyword = isOpen ? (student?.course?.trim().split(/\s+/)[0] || "") : "";
  useSearchExercises({
    manual: false,
    params: { text: defaultSearchKeyword, match: "exercise" },
    enabled: isOpen && !!student?.studentId,
    onSuccess: (res) => { if (res?.searchResults) setExerciseList(res.searchResults); },
  });

  const loadReviewHomework = useCallback(async (studentId) => {
    setIsLoadingReview(true);
    try {
      const res = await fetchReviewHomework({
        url: `/dashboard/homework/review/${studentId}`,
      });
      const homework = Array.isArray(res?.homework) ? res.homework : [];
      const mapped = homework.map((item) => {
        const isLink = item.type === "link";
        const isUpload = item.type === "upload";
        const displayText = isLink
          ? (item.meta || "Link")
          : isUpload
          ? "File upload"
          : (item.title || item.file || "-");
        const tooltipText = (isLink || isUpload)
          ? (item.file || "-")
          : (item.meta || item.file || "No description");
        return {
          ...item,
          assignedDate: item.createdAt ? moment(item.createdAt).format("DD MMM YYYY") : "-",
          isLink,
          isUpload,
          displayText,
          tooltipText,
        };
      });
      setReviewItems(mapped);
      setReviewCount(mapped.length);
    } catch (err) {
      console.error("Failed to load review homework:", err);
      toast.error("Failed to load homework history.");
    } finally {
      setIsLoadingReview(false);
    }
  }, [fetchReviewHomework]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !student?.studentId) return;
    loadReviewHomework(student.studentId);
  }, [isOpen, student?.studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setExerciseList([]);
    setSelectedExercises([]);
    setUrlArray([]);
    setUploadedFiles([]);
    setUrlValue("");
    setUrlTitle("");
    setSelectedAiHomework([]);
    setFailedTasksPopup(null);
    setReviewItems([]);
    setReviewCount(0);
  }, [student?.studentId]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchKey, matchType) => {
        try {
          const res = await searchExercises({ params: { text: searchKey, match: matchType } });
          if (res?.searchResults) setExerciseList(res.searchResults);
        } catch (err) {
          console.error("Exercise search error:", err);
        }
      }, 1000),
    [searchExercises]
  );

  const handleSearchExercise = useCallback(
    (query) => {
      if (!query || query.length < 2) return;
      let searchKey = query;
      let matchType = "exercise";
      if (query.includes("http")) {
        if (query.includes("scratch")) {
          const url = new URL(query);
          searchKey = url.searchParams.get("exerciseId");
        } else {
          const searchArr = query.split("/");
          searchKey = searchArr[searchArr.length - 1];
        }
        matchType = "exerciseUrl";
      }
      debouncedSearch(searchKey, matchType);
    },
    [debouncedSearch]
  );

  const handleSelectExercise = (list) => setSelectedExercises(list);
  const handleRemoveExercise = (filteredList) => setSelectedExercises(filteredList);

  const handleUrlValueChange = (e) => setUrlValue(e.target.value);
  const handleUrlTitleChange = (e) => setUrlTitle(e.target.value);

  const handleAddUrl = () => {
    if (!urlValue.trim() || !urlTitle.trim()) return;
    // { file, meta } matches the field names processUrlLinks expects
    setUrlArray((prev) => [...prev, { file: urlValue.trim(), meta: urlTitle.trim() }]);
    setUrlValue("");
    setUrlTitle("");
  };

  const handleRemoveUrl = (_e, urlItem) => {
    setUrlArray((prev) =>
      prev.filter((u) => u.meta !== urlItem.meta || u.file !== urlItem.file)
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Keep the original (pre-rename) name so duplicate uploads can be detected.
    setUploadedFiles((prev) => [
      ...prev,
      { file: file.name, fileObj: file, originalName: e.originalName || file.name },
    ]);
  };

  const handleRemoveFile = (fileItem) => {
    setUploadedFiles((prev) => prev.filter((f) => f.file !== fileItem.file));
  };

  // One AI entry per topic: re-adding the same topic replaces the existing entry
  // (latest difficulty/activity wins). No persisted-homeworkId locking here — the
  // dashboard picker always starts empty, so every entry is session-added.
  const handleAddAiHomework = (item) => {
    setSelectedAiHomework((prev) => {
      if (prev.some((hw) => hw?.id === item?.id)) return prev;
      const sameTopic = (hw) =>
        hw?.topicId != null &&
        item?.topicId != null &&
        String(hw.topicId) === String(item.topicId);
      return [...prev.filter((hw) => !sameTopic(hw)), item];
    });
  };

  const handleRemoveAiHomework = (item) => {
    setSelectedAiHomework((prev) => prev.filter((hw) => hw?.id !== item?.id));
  };

  // In-row difficulty/activity edits on a session-added AI homework entry.
  const handleUpdateAiHomework = (item, changes) => {
    setSelectedAiHomework((prev) =>
      prev.map((hw) => (hw?.id === item?.id ? { ...hw, ...changes } : hw))
    );
  };

  const handleAssign = async () => {
    const hasContent =
      selectedExercises.length > 0 ||
      urlArray.length > 0 ||
      uploadedFiles.length > 0 ||
      selectedAiHomework.length > 0;

    if (!hasContent) {
      toast.warn("Please add at least one homework item before assigning.");
      return;
    }

    setIsAssigning(true);
    try {
      const formData = new FormData();
      formData.append("studentId", student?.studentId);
      formData.append("batchId", student?.batchId);

      // homework must be a JSON string — backend parses it after multipart decode.
      // Exercise and AI homework share the `homework` array; links/files go in their
      // own fields (urlLinks / homeworkFiles).
      const homeworkArr = [];
      if (selectedExercises.length > 0) {
        homeworkArr.push({
          type: "exercise",
          data: selectedExercises.map((ex) => ({ exerciseId: ex.exerciseId })),
        });
      }
      if (selectedAiHomework.length > 0) {
        homeworkArr.push({
          type: "ai_homework",
          data: selectedAiHomework.map((hw) => ({
            topicId: hw.topicId,
            difficulty: hw.difficulty?.toLowerCase?.() ?? hw.difficulty,
            activity: hw.activity,
            ...(hw.testConfigId != null ? { testConfigId: hw.testConfigId } : {}),
          })),
        });
      }
      if (homeworkArr.length > 0) {
        formData.append("homework", JSON.stringify(homeworkArr));
      }

      if (urlArray.length > 0) {
        formData.append("urlLinks", JSON.stringify(urlArray));
      }

      uploadedFiles.forEach((f) => {
        if (f.fileObj) formData.append("homeworkFiles", f.fileObj);
      });

      const res = await assignHomework({
        data: formData,
        config: { headers: { "Content-Type": "multipart/form-data" } },
      });

      // Total business failure ({ msg, showMsg, failedHw:{ai:{}} }) — nothing persisted;
      // keep the inputs so the mentor can retry. Keyed off the explicit showMsg/success
      // signal so a success response that omits `success` isn't misread as an error.
      if (res?.showMsg || res?.success === false) {
        toast.error(res?.msg || "Failed to assign homework.");
        return;
      }

      // Partial persist: the good items are already saved; surface any AI homework
      // the backend could not assign (failedHw.ai = { topicId: reason }). Topic names
      // live only on the FE, so resolve them against the submitted list — falling back
      // to the topicId so every failed entry still shows a row.
      const failedAi = res?.failedHw?.ai || {};
      const failedItems = Object.keys(failedAi).map((topicId) => {
        const hw = selectedAiHomework.find(
          (h) => String(h?.topicId) === String(topicId)
        );
        return {
          id: `hw-${topicId}`,
          title: hw?.topicName || `Topic ${topicId}`,
          type: "homework",
        };
      });
      // Homework has persisted by this point — fully, or partially with the good
      // items already saved. The event lives here rather than on the "Homework"
      // choice in AssignAiTestFlow, which fires before anything is assigned.
      
      const assignedAiCount = selectedAiHomework.length - failedItems.length;
      if (assignedAiCount > 0) {
        fireEpicenterGtm("ai_assign_completed", [
          "homework",
          "",
          subject,
          assignedAiCount,
        ]);
      }

      if (failedItems.length > 0) {
        // FailedTasksModal is acknowledge-only; the form is cleared on confirm.
        setFailedTasksPopup({ items: failedItems });
        return;
      }

      toast.success(res?.message || "Homework assigned successfully!");
      setSelectedExercises([]);
      setUrlArray([]);
      setUploadedFiles([]);
      setSelectedAiHomework([]);

      if (student?.studentId) loadReviewHomework(student.studentId);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to assign homework.");
    } finally {
      setIsAssigning(false);
    }
  };

  // "Ignore & submit" (and the X) on the FailedTasksModal — the good items already
  // persisted server-side, so we don't resubmit; clear the form and refresh Review.
  const handleFailedTasksConfirm = () => {
    setFailedTasksPopup(null);
    setSelectedExercises([]);
    setUrlArray([]);
    setUploadedFiles([]);
    setSelectedAiHomework([]);
    if (student?.studentId) loadReviewHomework(student.studentId);
  };

  const handleCardClick = useCallback((item) => {
    if (item.isLink) {
      const url = item.file?.startsWith("http") ? item.file : `https://${item.file}`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (item.isUpload) {
      const a = document.createElement("a");
      a.href = item.file;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
      return;
    }
    // exercise → sandbox tracker
    if (!student?.studentId) return;
    openSandboxAsMentor({ redirect: `/tracker?studentId=${student.studentId}` }).catch(() =>
      toast.error("Failed to open sandbox tracker.")
    );
  }, [student?.studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    exerciseList,
    selectedExercises,
    urlArray,
    uploadedFiles,
    urlValue,
    urlTitle,
    selectedAiHomework,
    failedTasksPopup,
    reviewItems,
    reviewCount,
    isLoadingReview,
    handlers: {
      handleSearchExercise,
      handleSelectExercise,
      handleRemoveExercise,
      handleUrlValueChange,
      handleUrlTitleChange,
      handleAddUrl,
      handleRemoveUrl,
      handleFileChange,
      handleRemoveFile,
      handleAddAiHomework,
      handleRemoveAiHomework,
      handleUpdateAiHomework,
      handleAssign,
      handleFailedTasksConfirm,
      isAssigning,
      handleCardClick,
    },
  };
};

export default useHomeworkModal;
